import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate, Navigate } from "react-router-dom";
import {
  LayoutDashboard,
  Package,
  Receipt,
  Users,
  Warehouse,
  Tag,
  Truck,
  Megaphone,
  FileBarChart,
  FileText,
  Shield,
  LogOut,
  Search,
  AlertTriangle,
} from "lucide-react";
import api from "../../lib/api";
import { toast } from "sonner";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_c129d2ac-2209-4214-9ac4-af6d02f0e7d1/artifacts/e10f2m7m_hf_20260429_103436_b903d0ed-1980-49ff-95c4-cf93c75881ec.png";

const NAV = [
  { to: "/admin", label: "Dashboard", Icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/admin/products", label: "Products", Icon: Package, testid: "nav-products" },
  { to: "/admin/orders", label: "Orders", Icon: Receipt, testid: "nav-orders" },
  { to: "/admin/customers", label: "Customers", Icon: Users, testid: "nav-customers" },
  { to: "/admin/inventory", label: "Inventory", Icon: Warehouse, testid: "nav-inventory" },
  { to: "/admin/coupons", label: "Coupons", Icon: Tag, testid: "nav-coupons" },
  { to: "/admin/fleet", label: "Fleet", Icon: Truck, testid: "nav-fleet" },
  { to: "/admin/marketing", label: "Marketing", Icon: Megaphone, testid: "nav-marketing" },
  { to: "/admin/reports", label: "Reports", Icon: FileBarChart, testid: "nav-reports" },
  { to: "/admin/cms", label: "CMS", Icon: FileText, testid: "nav-cms" },
];

const SUPER_ADMIN_NAV = [
  { to: "/admin/staff", label: "Staff", Icon: Shield, testid: "nav-staff" },
];

export default function AdminLayout() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lowStockCount, setLowStockCount] = useState(0);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem("jow_token");
    if (!token) {
      setLoading(false);
      return;
    }
    api
      .get("/auth/me")
      .then((r) => {
        setMe(r.data);
        // Prefetch low stock count once
        if (["super_admin", "manager"].includes(r.data?.role)) {
          api
            .get("/admin/stats/overview?range=7d")
            .then((s) => setLowStockCount(s.data.low_stock_count || 0))
            .catch(() => {});
        }
      })
      .catch(() => localStorage.removeItem("jow_token"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-white label-overline text-jow-navy/50"
        data-testid="admin-loading"
      >
        Loading admin…
      </div>
    );
  }
  if (!me) return <Navigate to="/login?next=/admin" replace />;
  if (!["super_admin", "manager"].includes(me.role)) {
    return (
      <div
        className="min-h-screen flex flex-col items-center justify-center bg-jow-cream px-6 text-center"
        data-testid="admin-forbidden"
      >
        <Shield size={40} className="text-jow-pink mb-4" />
        <h1 className="font-display text-3xl text-jow-navy">Not for your eyes.</h1>
        <p className="mt-2 text-sm text-jow-navy/60 max-w-sm">
          This is the JewelleryOnWheels admin console. Please sign in with a manager or super-admin account.
        </p>
        <button onClick={() => navigate("/")} className="btn-secondary-jow mt-6">
          Back to storefront
        </button>
      </div>
    );
  }

  const signOut = () => {
    localStorage.removeItem("jow_token");
    toast.success("Signed out.");
    navigate("/");
  };

  const nav = me.role === "super_admin" ? [...NAV, ...SUPER_ADMIN_NAV] : NAV;

  return (
    <div className="min-h-screen bg-white flex" data-testid="admin-shell">
      {/* Sidebar */}
      <aside className="w-60 bg-jow-navy text-jow-cream flex flex-col sticky top-0 h-screen">
        <div className="px-5 py-5 flex items-center gap-3 border-b border-jow-cream/10">
          <img src={LOGO_URL} alt="JOW" className="h-8 w-8" />
          <div>
            <p className="font-display text-base leading-none">JOW Admin</p>
            <p className="label-overline text-jow-gold text-[9px] mt-1">Console</p>
          </div>
        </div>
        <nav className="flex-1 py-3 overflow-y-auto">
          {nav.map((t) => (
            <NavLink
              key={t.to}
              to={t.to}
              end={t.end}
              data-testid={t.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-5 py-2.5 text-[12px] uppercase tracking-[0.18em] transition-colors ${
                  isActive
                    ? "bg-jow-cream/10 text-jow-gold border-l-2 border-jow-gold"
                    : "text-jow-cream/70 hover:text-jow-cream hover:bg-jow-cream/5"
                }`
              }
            >
              <t.Icon size={14} /> {t.label}
            </NavLink>
          ))}
        </nav>
        <button
          onClick={signOut}
          className="px-5 py-4 border-t border-jow-cream/10 text-left text-[11px] uppercase tracking-[0.18em] text-jow-cream/70 hover:text-jow-pink inline-flex items-center gap-2"
          data-testid="admin-signout"
        >
          <LogOut size={13} /> Sign out
        </button>
      </aside>

      {/* Main column */}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="bg-white border-b border-jow-navy/10 px-6 py-3 flex items-center gap-4 sticky top-0 z-20">
          <div className="flex-1 flex items-center gap-2">
            <Search size={14} className="text-jow-navy/40" />
            <input
              type="text"
              placeholder="Quick search orders, products, customers…"
              className="flex-1 bg-transparent text-sm outline-none placeholder:text-jow-navy/40"
              data-testid="admin-quick-search"
              onKeyDown={(e) => {
                if (e.key === "Enter" && e.currentTarget.value) {
                  navigate(`/admin/orders?q=${encodeURIComponent(e.currentTarget.value)}`);
                }
              }}
            />
          </div>
          {lowStockCount > 0 && (
            <NavLink
              to="/admin/inventory"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-widest text-jow-pink"
              data-testid="admin-low-stock-banner"
            >
              <AlertTriangle size={13} /> {lowStockCount} SKUs low
            </NavLink>
          )}
          <div className="text-right">
            <p className="text-sm text-jow-navy" data-testid="admin-me-name">
              {me.name || me.phone}
            </p>
            <p
              className="label-overline text-jow-gold text-[9px]"
              data-testid="admin-me-role"
            >
              {me.role.replace("_", " ")}
            </p>
          </div>
        </header>

        <main className="flex-1 bg-jow-cream/30 px-6 py-6" data-testid="admin-main">
          <Outlet context={{ me }} />
        </main>
      </div>
    </div>
  );
}
